/**
 * <Card> — surface container with locked padding, radius, and border.
 *
 * Card titles render as <Heading level={3}> per the locked typography
 * cascade — same visual treatment whether the card lives on a page,
 * inside a drawer, modal, or dashboard widget. Card subtitles use
 * <Text variant="body-sm" tone="muted">.
 *
 * Composition pattern (use these together):
 *
 *   <Card>
 *     <Card.Header title="Recent Work Orders" actions={<Button>...</Button>} />
 *     <Card.Body>...</Card.Body>
 *     <Card.Footer>...</Card.Footer>
 *   </Card>
 *
 * Replaces the 18 feature-specific *Card*.tsx files (Phase-1 §1.2 / M17)
 * and the 50+ ad-hoc `<div className="rounded-lg border bg-surface...">`
 * patterns. The legacy `frontend/src/components/ui/Card.tsx` stays
 * available during the migration window (Phase 13 cleanup).
 *
 * Spec: docs/design-system/03-spacing-layout.md, 05-components-overview.md.
 */

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../utils/cn';
import { Heading } from './Heading';
import { Text } from './Text';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Body padding. Default `md` (16px). Use `none` when the card hosts a table or list directly. */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Visual emphasis. */
  variant?: 'default' | 'elevated' | 'subtle' | 'outline';
  /** Hover lift / border emphasis. */
  interactive?: boolean;
  children: ReactNode;
}

const PADDING_CLASSES: Record<NonNullable<CardProps['padding']>, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

const VARIANT_CLASSES: Record<NonNullable<CardProps['variant']>, string> = {
  default: 'bg-surface border border-border shadow-card',
  elevated: 'bg-surface border border-border shadow-md',
  subtle: 'bg-surface-elevated border border-border-subtle',
  outline: 'bg-surface border border-border',
};

const CardRoot = forwardRef<HTMLDivElement, CardProps>(function Card(
  { padding = 'md', variant = 'default', interactive = false, className, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        /* `smms-card` is a stable, style-free hook (mirrors Badge's
           `smms-badge`) for the A11Y-0006 forced-colors override in
           tokens/global.css — Card carries no other class/attribute a CSS
           selector could target without one. */
        'smms-card rounded-lg overflow-hidden',
        VARIANT_CLASSES[variant],
        PADDING_CLASSES[padding],
        interactive && 'transition-colors hover:border-border-strong cursor-pointer',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
});
CardRoot.displayName = 'Card';

interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Card title — emits <Heading level={3}>. */
  title?: ReactNode;
  /** Sub-line under the title. */
  description?: ReactNode;
  /** Eyebrow above the title. */
  eyebrow?: ReactNode;
  /** Right-aligned actions. */
  actions?: ReactNode;
}

function CardHeader({ title, description, eyebrow, actions, className, children, ...rest }: CardHeaderProps) {
  if (children) {
    return (
      <div className={cn('flex items-center justify-between gap-3', className)} {...rest}>
        {children}
      </div>
    );
  }
  return (
    <div className={cn('flex items-start justify-between gap-3 mb-3', className)} {...rest}>
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        {eyebrow && <Text variant="eyebrow" tone="muted">{eyebrow}</Text>}
        {title && <Heading level={3}>{title}</Heading>}
        {description && (
          <Text variant="body-sm" tone="muted">
            {description}
          </Text>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>
  );
}
CardHeader.displayName = 'Card.Header';

function CardBody({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('', className)} {...rest}>
      {children}
    </div>
  );
}
CardBody.displayName = 'Card.Body';

function CardFooter({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mt-3 pt-3 border-t border-border-subtle flex items-center justify-end gap-2', className)}
      {...rest}
    >
      {children}
    </div>
  );
}
CardFooter.displayName = 'Card.Footer';

type CardComponent = typeof CardRoot & {
  Header: typeof CardHeader;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
};

export const Card = CardRoot as CardComponent;
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;

export default Card;
